/**
 * M6.5-W6 — "Verplaats naar folder…" dialog.
 *
 * Multi-select in non-Base Explorer → klik "Verplaats naar folder…" →
 * deze dialog opent. Toont alle folders in Base, gefilterd op type_group
 * match — alleen folders waar ALLE geselecteerde concepten in passen
 * (= dezelfde resolved folder-class voor élk).
 *
 * Geen write-access-filter: Base-flows zijn admin-only (Base zelf is
 * onzichtbaar voor non-admins), en admin mag overal heen. Server
 * (`POST /folders/:fid/move-in`) enforced de typeGroup-match en de
 * write-access-check voor non-admin paden alsnog.
 *
 * Resolve folder-class via resolveFolderClassForConcept op elk
 * geselecteerd concept; bij mismatch toont de dialog een waarschuwing
 * dat de geselecteerde set gemengde type-classes heeft → niet één
 * folder kan ze allemaal hebben.
 */
import { resolveFolderClassForConcept } from "@ae/domain";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { ApiError, api, type FolderDTO } from "../../api/client";
import { resolveBilingual } from "../../registry";
import { useWorkspaceData } from "../../state/workspace";

interface Props {
  workspaceId: string;
  conceptIds: readonly string[];
  lang: string;
  /** Callback. `refresh=true` betekent: tree moet ververst worden (er
   *  zijn concepten verhuisd). */
  onClose: (refresh: boolean) => void;
}

export function MoveToFolderDialog({ workspaceId, conceptIds, lang, onClose }: Props) {
  const { t } = useTranslation();
  const [folders, setFolders] = useState<FolderDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [pickedFolderId, setPickedFolderId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .listFolders(workspaceId)
      .then((r) => {
        if (cancelled) return;
        setFolders(r.folders);
      })
      .catch((err) => {
        if (!cancelled) {
          const msg = err instanceof ApiError ? (err.body?.detail ?? err.message) : String(err);
          setError(msg);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  // Resolveer de folder-class van elk geselecteerd concept.
  const conceptClasses = useMemo(() => {
    const store = useWorkspaceData.getState().store;
    const set = new Set<string>();
    for (const id of conceptIds) {
      const c = store.getConcept(id);
      if (c) set.add(resolveFolderClassForConcept(c.typeId, c.stereotypes));
    }
    return set;
  }, [conceptIds]);

  // Toegestane folder-classes voor de selectie. Bij gemengde set:
  // geen folder past, dialog toont waarschuwing.
  const allowedClass: string | null =
    conceptClasses.size === 1 ? (Array.from(conceptClasses)[0] ?? null) : null;

  const matchingFolders = useMemo(() => {
    if (!allowedClass) return [];
    return folders.filter((f) => f.typeGroup === allowedClass);
  }, [folders, allowedClass]);

  const handleConfirm = async () => {
    if (!pickedFolderId || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.moveConceptsToFolder(workspaceId, pickedFolderId, [...conceptIds]);
      if (res.skipped.length > 0) {
        setError(
          t("moveToFolder.partial", {
            moved: res.moved.length,
            skipped: res.skipped.length,
            reason: res.skipped[0]?.reason ?? t("moveToFolder.unknown"),
          }),
        );
        return;
      }
      onClose(true);
    } catch (err) {
      const msg = err instanceof ApiError ? (err.body?.detail ?? err.message) : String(err);
      setError(t("moveToFolder.failed", { msg }));
    } finally {
      setSubmitting(false);
    }
  };

  const node = (
    // biome-ignore lint/a11y/noStaticElementInteractions: presentationele overlay-laag; sluiten via Escape + knoppen, klik-buiten is muis-gemak
    <div
      className="ledger fixed inset-0 z-[100] flex items-center justify-center bg-black/30"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose(false);
      }}
      role="presentation"
    >
      <div
        className="w-96 max-w-[90vw] rounded-lg border border-(--ld-control) bg-(--ld-surface) shadow-xl"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="border-(--ld-border) border-b px-4 py-2 font-semibold text-(--ld-text) text-sm">
          {t("moveToFolder.title", { count: conceptIds.length })}
        </header>
        <div className="max-h-72 overflow-y-auto px-3 py-3 text-sm">
          {loading ? (
            <p className="text-(--ld-muted) italic">{t("foldertree.loadingFolders")}</p>
          ) : !allowedClass ? (
            <p className="text-(--ld-notice-ink)">
              {t("moveToFolder.mixedClasses", { classes: Array.from(conceptClasses).join(", ") })}
            </p>
          ) : matchingFolders.length === 0 ? (
            <p className="text-(--ld-muted) italic">
              {t("moveToFolder.noFoldersBefore")} <code className="font-mono">{allowedClass}</code>{" "}
              {t("moveToFolder.noFoldersAfter")}
            </p>
          ) : (
            <ul className="flex flex-col gap-1">
              {matchingFolders.map((f) => {
                const picked = pickedFolderId === f.id;
                return (
                  <li key={f.id}>
                    <button
                      type="button"
                      onClick={() => setPickedFolderId(f.id)}
                      disabled={submitting}
                      className={`block w-full rounded border px-2 py-1.5 text-left disabled:opacity-50 ${
                        picked
                          ? "border-(--ld-accent) bg-(--ld-active) text-(--ld-accent)"
                          : "border-(--ld-border) hover:bg-(--ld-raised)"
                      }`}
                    >
                      <span className="font-medium">{f.name}</span>
                      <span className="ml-2 text-(--ld-muted) text-xs">({f.typeGroup})</span>
                      {f.description && (
                        <span className="ml-2 truncate text-(--ld-muted) text-xs italic">
                          {resolveBilingual({ nl: f.description, en: f.description }, lang)}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {error && (
            <p className="mt-3 rounded border border-(--ld-control) bg-(--ld-negative-bg) px-2 py-1 text-(--ld-negative-ink) text-xs">
              {error}
            </p>
          )}
        </div>
        <footer className="flex justify-end gap-2 border-(--ld-border) border-t bg-(--ld-raised) px-3 py-2">
          <button
            type="button"
            onClick={() => onClose(false)}
            className="rounded border border-(--ld-control) bg-(--ld-surface) px-3 py-1 text-(--ld-text) text-xs hover:bg-(--ld-raised)"
          >
            {t("settings.common.cancel")}
          </button>
          <button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={!pickedFolderId || submitting}
            className="rounded bg-(--ld-primary) px-3 py-1 text-(--ld-primary-ink) text-xs hover:bg-(--ld-primary-hover) disabled:opacity-50"
          >
            {t("moveToFolder.confirm")}
          </button>
        </footer>
      </div>
    </div>
  );
  return createPortal(node, document.body);
}
